"use client";

import { useCallback, useMemo, useState } from "react";
import { Chess, type Color, type PieceSymbol, type Square } from "chess.js";
import { parsePlacement } from "@/lib/variants/fen";

export type Orientation = "white" | "black";
export type Movable = "white" | "black" | "both" | "none";
/** A board gesture. For a drop, `drop` names the piece and `from` equals `to`. */
export type MoveIntent = { from: Square; to: Square; promotion?: PieceSymbol; drop?: PieceSymbol };
export type Arrow = { from: Square; to: Square; color?: string };

/**
 * Rules supplied by the caller instead of chess.js, for chess variants: the board reads
 * pieces from the FEN and allows exactly these moves.
 */
export type BoardRules = {
  /** UCI moves: "e2e4", "e7e8q", drops "P@e4", Chess960 castling as king-onto-rook ("e1h1"). */
  legal: readonly string[];
  checkSquare?: string | null;
  /** A pocket piece the player picked; its legal drop squares become the targets. */
  dropPiece?: PieceSymbol | null;
  /** Called when the player clicks somewhere that is not a drop square. */
  onDropCancel?: () => void;
};

export const FILES = ["a", "b", "c", "d", "e", "f", "g", "h"] as const;

export const PIECE_LABEL: Record<PieceSymbol, string> = {
  k: "king",
  q: "queen",
  r: "rook",
  b: "bishop",
  n: "knight",
  p: "pawn",
};

const PROMOTION_ORDER: PieceSymbol[] = ["q", "r", "b", "n", "k"];

/** Squares in display order (top-left first) for the given orientation. */
export function squaresFor(orientation: Orientation): Square[] {
  const out: Square[] = [];
  for (let r = 0; r < 8; r++)
    for (let f = 0; f < 8; f++) {
      const file = orientation === "white" ? f : 7 - f;
      const rank = orientation === "white" ? 8 - r : r + 1;
      out.push(`${FILES[file]}${rank}` as Square);
    }
  return out;
}

export function isLightSquare(sq: Square): boolean {
  const file = sq.charCodeAt(0) - 97;
  const rank = Number(sq[1]) - 1;
  return (file + rank) % 2 === 1;
}

type Target = { capture: boolean };

/** Everything the board needs to know about a position, from chess.js or from supplied rules. */
type PositionModel = {
  turn: Color;
  pieceAt: (sq: Square) => { type: PieceSymbol; color: Color } | null;
  movesFrom: (sq: Square) => Map<Square, Target>;
  promotionsFor: (from: Square, to: Square) => PieceSymbol[];
  dropTargets: (piece: PieceSymbol) => Map<Square, Target>;
  checkSquare: Square | null;
};

function chessModel(fen: string): PositionModel {
  let chess: Chess;
  try {
    chess = new Chess(fen);
  } catch {
    chess = new Chess();
  }
  const turn = chess.turn();
  return {
    turn,
    pieceAt: (sq) => chess.get(sq) ?? null,
    movesFrom: (sq) => {
      const m = new Map<Square, Target>();
      for (const mv of chess.moves({ square: sq, verbose: true })) m.set(mv.to, { capture: !!mv.captured });
      return m;
    },
    promotionsFor: (from, to) => (chess.moves({ square: from, verbose: true }).some((m) => m.to === to && m.promotion) ? ["q", "r", "b", "n"] : []),
    dropTargets: () => new Map(),
    checkSquare: chess.inCheck() ? chess.findPiece({ type: "k", color: turn })[0] ?? null : null,
  };
}

function rulesModel(fen: string, rules: BoardRules): PositionModel {
  const pieces = parsePlacement(fen);
  const turn: Color = fen.split(" ")[1] === "b" ? "b" : "w";
  const pieceAt = (sq: Square) => {
    const p = pieces.get(sq);
    return p ? { type: p.type, color: p.color } : null;
  };
  return {
    turn,
    pieceAt,
    movesFrom: (sq) => {
      const m = new Map<Square, Target>();
      for (const uci of rules.legal) {
        if (uci[1] === "@" || uci.slice(0, 2) !== sq) continue;
        const to = uci.slice(2, 4) as Square;
        const occupant = pieces.get(to);
        // Chess960 castling lands on your own rook; that is not a capture.
        m.set(to, { capture: !!occupant && occupant.color !== turn });
      }
      return m;
    },
    promotionsFor: (from, to) => {
      const found = new Set(rules.legal.filter((u) => u.length === 5 && u.slice(0, 4) === `${from}${to}`).map((u) => u[4] as PieceSymbol));
      return PROMOTION_ORDER.filter((p) => found.has(p));
    },
    dropTargets: (piece) => {
      const m = new Map<Square, Target>();
      for (const uci of rules.legal) if (uci[1] === "@" && uci[0].toLowerCase() === piece) m.set(uci.slice(2, 4) as Square, { capture: false });
      return m;
    },
    checkSquare: (rules.checkSquare as Square | null | undefined) ?? null,
  };
}

/**
 * Click-to-move state machine shared by the 2D and 3D boards: first click selects
 * one of your pieces, the second click on a highlighted square moves. Promotions
 * pause for a piece choice. With `rules`, the position can be any 8×8 chess variant.
 */
export function useBoardInteraction(fen: string, movable: Movable, onMove?: (m: MoveIntent) => boolean | void, rules?: BoardRules) {
  const [selected, setSelected] = useState<Square | null>(null);
  const [pendingPromotion, setPendingPromotion] = useState<{ from: Square; to: Square; choices: PieceSymbol[] } | null>(null);

  const model = useMemo(() => (rules ? rulesModel(fen, rules) : chessModel(fen)), [fen, rules]);
  const { turn } = model;
  const dropPiece = rules?.dropPiece ?? null;

  const canMoveColor = useCallback(
    (color: Color) =>
      movable === "both" || (movable === "white" && color === "w") || (movable === "black" && color === "b"),
    [movable],
  );

  const targets = useMemo(() => {
    if (dropPiece && canMoveColor(turn)) return model.dropTargets(dropPiece);
    if (!selected) return new Map<Square, Target>();
    return model.movesFrom(selected);
  }, [model, selected, dropPiece, canMoveColor, turn]);

  const [prevFen, setPrevFen] = useState(fen);
  if (prevFen !== fen) {
    // Position changed from outside (bot move, navigation): drop any selection.
    setPrevFen(fen);
    setSelected(null);
    setPendingPromotion(null);
  }

  const canSelect = useCallback(
    (sq: Square) => {
      if (movable === "none") return false;
      const p = model.pieceAt(sq);
      return !!p && p.color === turn && canMoveColor(p.color);
    },
    [model, movable, turn, canMoveColor],
  );

  const tryMove = useCallback(
    (from: Square, to: Square): boolean => {
      if (!model.movesFrom(from).has(to)) return false;
      const choices = model.promotionsFor(from, to);
      if (choices.length > 0) {
        setPendingPromotion({ from, to, choices });
        return true;
      }
      const accepted = onMove?.({ from, to });
      if (accepted !== false) setSelected(null);
      return accepted !== false;
    },
    [model, onMove],
  );

  const clickSquare = useCallback(
    (sq: Square) => {
      if (pendingPromotion) return;
      if (dropPiece) {
        if (targets.has(sq)) {
          onMove?.({ from: sq, to: sq, drop: dropPiece });
          return;
        }
        rules?.onDropCancel?.();
        setSelected(canSelect(sq) ? sq : null);
        return;
      }
      if (selected && selected !== sq && targets.has(sq)) {
        tryMove(selected, sq);
        return;
      }
      if (selected === sq) {
        setSelected(null);
        return;
      }
      setSelected(canSelect(sq) ? sq : null);
    },
    [pendingPromotion, dropPiece, selected, targets, tryMove, canSelect, onMove, rules],
  );

  const choosePromotion = useCallback(
    (piece: PieceSymbol | null) => {
      const p = pendingPromotion;
      setPendingPromotion(null);
      setSelected(null);
      if (p && piece) onMove?.({ from: p.from, to: p.to, promotion: piece });
    },
    [pendingPromotion, onMove],
  );

  return {
    pieceAt: model.pieceAt,
    turn,
    selected,
    setSelected,
    targets,
    canSelect,
    clickSquare,
    tryMove,
    pendingPromotion,
    choosePromotion,
    checkSquare: model.checkSquare,
  };
}
